import * as browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { CommandEventType, CommandName, getFragment, isFutureFragment, } from "../../../src/ipm/background";
import * as logger from "../../utilities/background";
import { getSettings, settings } from "../../prefs/background/settings";
import { CreationMethod, isNewTabBehavior, setNewTabCommandHandler } from "./middleware";
import { NewTabEventType, NewTabErrorEventType, NewTabExitEventType, blockCountQueryParameter, lastShownKey, coolDownPeriodKey, } from "./tab-manager.types";
import { Prefs } from "../../alias/prefs";
const onCreatedHandlerByIPMids = new Map();
const onUpdatedHandlerByIPMids = new Map();
let newTabCounter = 0;
const newTabMessageSendTrigger = [1, 10, 20, 100, 200];
let waitingTabs = {};
const tabIds = new Set();
const onNewTabUpdateHandlerByIpmId = new Map();
const createdTabsMap = new Map();
const serverLogger = new ewe.telemetry.ServerLogger("new_tab");
export function compareNewTabRequestsByPriority(newTabA, newTabB) {
    if (newTabA.behavior.method === CreationMethod.force &&
        newTabB.behavior.method !== CreationMethod.force) {
        return -1;
    }
    if (newTabA.behavior.method !== CreationMethod.force &&
        newTabB.behavior.method === CreationMethod.force) {
        return 1;
    }
    if (newTabA.behavior.priority > newTabB.behavior.priority) {
        return -1;
    }
    if (newTabA.behavior.priority < newTabB.behavior.priority) {
        return 1;
    }
    if (newTabA.ipmId < newTabB.ipmId) {
        return -1;
    }
    if (newTabA.ipmId > newTabB.ipmId) {
        return 1;
    }
    return 0;
}
function recordLegacyEvent(ipmId, name) {
    getFragment().recordEvent(ipmId, CommandName.createTab, name);
}
export async function isCoolDownPeriodOngoing() {
    await Prefs.untilLoaded;
    return Prefs.get(lastShownKey) + Prefs.get(coolDownPeriodKey) > Date.now();
}
const openNewtabOnUpdated = (tabId, changeInfo, tab) => {
    if (changeInfo.status !== "complete" ||
        tab === null ||
        tabId !== tab.id ||
        !createdTabsMap.has(tabId)) {
        return;
    }
    const ipmId = createdTabsMap.get(tabId);
    if (typeof ipmId !== "string") {
        return;
    }
    const onUpdatedHandler = onNewTabUpdateHandlerByIpmId.get(ipmId);
    onNewTabUpdateHandlerByIpmId.delete(ipmId);
    browser.tabs.onUpdated.removeListener(onUpdatedHandler);
    if (isFutureFragment()) {
        serverLogger.info("tab_loaded", ipmId);
    }
    recordLegacyEvent(ipmId, NewTabEventType.loaded);
};
async function addBlockCountToURL(urlString) {
    let url;
    try {
        url = new URL(urlString);
    }
    catch (_) {
        return null;
    }
    await Prefs.untilLoaded;
    const blockCount = Prefs.get("blocked_total");
    url.searchParams.append(blockCountQueryParameter, blockCount);
    return url.toString();
}
async function openNewtab() {
    logger.debug("[new-tab]:openNewtab");
    const candidates = Object.values(waitingTabs).sort(compareNewTabRequestsByPriority);
    for (const candidate of candidates) {
        logger.debug("[new-tab]:openNewtab");
        const { ipmId } = candidate;
        const { addCohortAssignmentsSearchParam, addExperimentsSearchParams, checkLanguage, createSafeOriginUrl, dismissCommand, doesLicenseStateMatch, getBehavior, getCommand, isCommandExpired, } = getFragment();
        void checkLanguage(ipmId);
        removeListeners(ipmId);
        if (await isCoolDownPeriodOngoing()) {
            logger.debug("[new-tab]: Cool down period still ongoing");
            return;
        }
        const behavior = getBehavior(ipmId);
        if (!isNewTabBehavior(behavior)) {
            logger.debug("[new-tab]: No command behavior");
            recordLegacyEvent(ipmId, NewTabErrorEventType.noBehaviorFound);
            dismissCommand(ipmId);
            continue;
        }
        if (!(await doesLicenseStateMatch(behavior))) {
            logger.debug("[new-tab]: License state mis-match");
            recordLegacyEvent(ipmId, NewTabErrorEventType.licenseStateNoMatch);
            dismissCommand(ipmId);
            continue;
        }
        const targetUrl = createSafeOriginUrl(behavior.target);
        if (!targetUrl) {
            logger.debug("[new-tab]: Invalid target URL.");
            recordLegacyEvent(ipmId, NewTabErrorEventType.noUrlFound);
            dismissCommand(ipmId);
            continue;
        }
        let url = await addExperimentsSearchParams(targetUrl);
        if (url === null) {
            logger.debug("[new-tab]: Failed to append experiments search params.");
            url = targetUrl;
        }
        url = await addCohortAssignmentsSearchParam(targetUrl);
        if (url === null) {
            logger.debug("[new-tab]: Failed to append cohort assignments search param.");
            url = targetUrl;
        }
        url = await addBlockCountToURL(url);
        if (url === null) {
            getFragment().recordEvent(ipmId, CommandName.createTab, NewTabErrorEventType.noUrlFound);
            dismissCommand(ipmId);
            logger.debug("[new-tab]: Invalid target URL.");
            continue;
        }
        const command = getCommand(ipmId);
        if (!command) {
            continue;
        }
        if (isCommandExpired(command)) {
            logger.error("[new-tab]: Command has expired.");
            recordLegacyEvent(ipmId, CommandEventType.expired);
            dismissCommand(ipmId);
            continue;
        }
        const loadEventListener = openNewtabOnUpdated.bind(null);
        browser.tabs.onUpdated.addListener(loadEventListener);
        let tabId;
        try {
            const tab = await browser.tabs.create({ url });
            tabId = tab.id;
        }
        catch (ex) {
            logger.error(`[new-tab]: Tab creation error for IPM id "${ipmId}"`, ex);
            recordLegacyEvent(ipmId, NewTabErrorEventType.tabCreationError);
            continue;
        }
        if (typeof tabId === "undefined") {
            logger.error(`[new-tab]: Invalid ID "${tabId}" for created tab for IPM ID "${ipmId}"`);
            recordLegacyEvent(ipmId, NewTabErrorEventType.tabCreationError);
            continue;
        }
        createdTabsMap.set(tabId, ipmId);
        onNewTabUpdateHandlerByIpmId.set(ipmId, loadEventListener);
        await Prefs.set(lastShownKey, Date.now());
        if (isFutureFragment()) {
            serverLogger.info("tab_created", ipmId);
        }
        recordLegacyEvent(ipmId, NewTabEventType.created);
        dismissCommand(ipmId);
    }
}
function removeListeners(ipmId) {
    const onCreatedHandler = onCreatedHandlerByIPMids.get(ipmId);
    onCreatedHandlerByIPMids.delete(ipmId);
    const onUpdatedHandler = onUpdatedHandlerByIPMids.get(ipmId);
    onUpdatedHandlerByIPMids.delete(ipmId);
    if (typeof onCreatedHandler !== "undefined") {
        browser.tabs.onCreated.removeListener(onCreatedHandler);
        browser.tabs.onUpdated.removeListener(onUpdatedHandler);
    }
    browser.tabs.onRemoved.removeListener(onRemoved);
}
const openNotificationTab = () => {
    tabIds.clear();
    openNewtab();
};
const onCreated = (tab) => {
    if (tab.url === "about:newtab") {
        openNotificationTab();
        return;
    }
    if (typeof tab.id !== "number") {
        return;
    }
    tabIds.add(tab.id);
};
const onRemoved = (tabId) => {
    createdTabsMap.delete(tabId);
    tabIds.delete(tabId);
};
const onUpdated = (tabId, changeInfo, tab) => {
    if (!tabIds.has(tabId) || changeInfo.status !== "complete") {
        return;
    }
    tabIds.delete(tabId);
    if (tab.url && /^https?:/.test(tab.url)) {
        newTabCounter += 1;
        const ipmId = createdTabsMap.get(tabId);
        if (typeof ipmId === "string" && newTabMessageSendTrigger.includes(newTabCounter)) {
            getFragment().recordEvent(ipmId, CommandName.createTab, `${NewTabEventType.has_content}:${newTabCounter}`);
        }
        return;
    }
    openNotificationTab();
};
async function handleCommand(ipmId) {
    logger.debug("[new-tab]:tab manager handleCommand", ipmId);
    const { dismissCommand, getBehavior } = getFragment();
    const { installType } = await browser.management.getSelf();
    if (installType === "admin") {
        recordLegacyEvent(ipmId, NewTabExitEventType.admin);
        dismissCommand(ipmId);
        return;
    }
    await settings.onload();
    if (getSettings().suppress_update_page) {
        logger.debug("[new-tab]:suppress_update_page - true");
        recordLegacyEvent(ipmId, NewTabExitEventType.disabled);
        dismissCommand(ipmId);
        return;
    }
    logger.debug("[new-tab]:openNewtab");
    const behavior = getBehavior(ipmId);
    if (!isNewTabBehavior(behavior)) {
        logger.debug("[new-tab]: Invalid command behavior");
        recordLegacyEvent(ipmId, NewTabErrorEventType.noBehaviorFound);
        dismissCommand(ipmId);
        return;
    }
    recordLegacyEvent(ipmId, NewTabEventType.received);
    waitingTabs[ipmId] = { behavior, ipmId };
    if (isFutureFragment()) {
        serverLogger.info("command_ready", ipmId);
    }
    if (behavior.method === CreationMethod.force) {
        return;
    }
    const onCreatedHandler = onCreated.bind(null);
    onCreatedHandlerByIPMids.set(ipmId, onCreatedHandler);
    const onUpdatedHandler = onUpdated.bind(null);
    onUpdatedHandlerByIPMids.set(ipmId, onUpdatedHandler);
    browser.tabs.onCreated.addListener(onCreatedHandler);
    browser.tabs.onRemoved.addListener(onRemoved);
    browser.tabs.onUpdated.addListener(onUpdatedHandler);
    newTabCounter = 0;
}
function onCommandsProcessed() {
    if (Array.from(Object.values(waitingTabs)).some((candidate) => candidate.behavior.method === CreationMethod.force)) {
        void openNewtab();
    }
}
async function clearCommands() {
    for (const ipmId of Object.keys(waitingTabs)) {
        removeListeners(ipmId);
    }
    waitingTabs = {};
    for (const [tabID, ipmId] of createdTabsMap) {
        const updateListener = onNewTabUpdateHandlerByIpmId.get(ipmId);
        if (updateListener) {
            browser.tabs.onUpdated.removeListener(updateListener);
        }
        try {
            await browser.tabs.remove(tabID);
        }
        catch (ex) {
        }
    }
    onNewTabUpdateHandlerByIpmId.clear();
    createdTabsMap.clear();
    tabIds.clear();
}
export async function start() {
    logger.debug("[new-tab]:tab manager start");
    setNewTabCommandHandler(handleCommand, clearCommands, onCommandsProcessed);
}
